import React, { useState, useEffect, useRef } from "react";
import {
  API_BASE_URL,
  ACCESS_TOKEN_NAME,
} from "../../constants/apiConstants";
import { AuthContext } from "../../contexts/auth.contexts";
import request from "../../utils/Request";
import Button from "react-bootstrap/Button";
import "./ManageUsers.css";
import Modal from "./../Modal/Modal.js";
import ModalApproval from "./../ModalApproval/ModalApproval.js";
import ModalActivate from "./../ModalActivate/ModalActivate.js";
import ModalPasswordChange from "./../ModalPasswordChange/ModalPasswordChange.js";
import ModalVerify from "./../ModalVerify/ModalVerify.js";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import TextInput, { PassWordInput } from "./../common/TextInput";
import PermissionGate from "../../contexts/PermissionGate";
import { FormCheck } from "react-bootstrap";
import ChangePassword from "./ChangePassword";
import LOADING from "../../tube-spinner.svg";
import DefaultMaleImage from "../../male-default-profile-picture.png";
import DefaultFemaleImage from "../../female-default-profile-picture.png";
import axios from "axios";
import Dropdown from "react-bootstrap/Dropdown";
import { useTranslation } from "react-i18next";

const getValidationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().required(t("nameRequired")),
    email: Yup.string().email(t("invalidEmail")).required(t("emailRequired")),
    password: Yup.string().min(8, t("passwordMin")).required(t("passwordRequired")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], t("passwordsMustMatch"))
      .required(t("confirmPasswordRequired")),
  });

const USERS_PER_PAGE = 50;
const SEARCH_DEBOUNCE_MS = 350;

function ManageAdmin() {
  const { t, i18n } = useTranslation();

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [debouncedEmail, setDebouncedEmail] = useState("");

  const [modalState, setModalState] = useState(false);
  const [modalStateApproval, setModalStateApproval] = useState(null);
  const [modalStateActivate, setModalStateActivate] = useState(false);
  const [modalStatePassword, setModalStatePassword] = useState(null);
  const [modalStateVerfiy, setModalStateVerify] = useState(null);
  const [modalStateChangeRole, setModalStateChangeRole] = useState(false);
  const [changeRoleUserId, setChangeRoleUserId] = useState(false);

  const { authState, authActions } = React.useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [rolesforusers, setRolesforUsers] = useState([]);
  const [menuOpen, setMenuOpen] = useState([]);

  const totalPages = Math.max(1, Math.ceil(total / USERS_PER_PAGE));
  const hasActiveSearch = debouncedName !== "" || debouncedEmail !== "";

  // Sync language from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langFromUrl = urlParams.get("lang");
    if (langFromUrl && ["en", "fi", "sv"].includes(langFromUrl)) {
      i18n.changeLanguage(langFromUrl);
    }
  }, [i18n]);

  // Debounce name
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(searchName.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchName]);

  // Debounce email
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEmail(searchEmail.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchEmail]);

  // Reset to page 1 on search change (skip first render)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
  }, [debouncedName, debouncedEmail]);

  // Fetch on page or search change
  useEffect(() => {
    fetchUsers(page, debouncedName, debouncedEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedName, debouncedEmail]);

  const fetchUsers = async (pageNumber, name, email) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({ page: pageNumber, per_page: USERS_PER_PAGE });
      if (name) params.append("name", name);
      if (email) params.append("email", email);

      const response = await axios.get(
        `${API_BASE_URL}/api/manage/users?${params.toString()}`,
        { headers: { Authorization: "Bearer " + localStorage.getItem(ACCESS_TOKEN_NAME) } }
      );

      const responseData = response.data;
      if (Array.isArray(responseData)) {
        setUsers(responseData);
        setTotal(
          responseData.length < USERS_PER_PAGE
            ? (pageNumber - 1) * USERS_PER_PAGE + responseData.length
            : pageNumber * USERS_PER_PAGE + 1
        );
      } else {
        setUsers(responseData.data ?? []);
        setTotal(responseData.total ?? 0);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUsers = () => fetchUsers(page, debouncedName, debouncedEmail);

  const clearSearch = () => {
    setSearchName("");
    setSearchEmail("");
  };

  const handleToggle = (index) => {
    setMenuOpen((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  useEffect(() => {
    const fetchRolesForAdd = async () => {
      try {
        const res = await request().get("/api/manage/roles/foradd");
        setRolesforUsers(res.data);
      } catch (error) { console.error(error); }
    };
    const fetchAllRoles = async () => {
      try {
        const res = await request().get("/api/manage/roles/all");
        setRoles(res.data);
      } catch (error) { console.error(error); }
    };
    fetchRolesForAdd();
    fetchAllRoles();
  }, []);

  const changeRole = (values) => {
    request()
      .post("/api/manage/roles/setRole", { ...values, userid: changeRoleUserId })
      .then(() => { setModalStateChangeRole(false); refreshUsers(); });
  };

  const userStatusHandler = () => {
    request()
      .post("/api/manage/users/change-status", { id: modalStateApproval })
      .then(() => { setModalStateApproval(null); refreshUsers(); });
  };

  const userVerifyHandler = () => {
    request()
      .post("/api/manage/users/verify", { id: modalStateVerfiy })
      .then(() => { setModalStateVerify(null); refreshUsers(); });
  };

  const userPasswordHandler = (values) => {
    request()
      .post("/api/manage/users/change-password", { id: modalStatePassword, ...values })
      .then(() => { setModalStatePassword(null); refreshUsers(); });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };



  return (
    <>
      {/* Add User Modal */}
      <Modal show={modalState}>
        <div>
          <h1>{t("addUser")}</h1>
          <Formik
            initialValues={{ name: "", gender: "male", email: "", role: "", password: "", confirmPassword: "" }}
            validationSchema={getValidationSchema(t)}
            onSubmit={(values) => {
              request()
                .post("/api/manage/users/add-user", { ...values })
                .then(() => { setModalState(false); refreshUsers(); });
            }}
          >
            {({ submitForm }) => (
              <form className="row">
                <div className="col-12">
                  <TextInput placeholder={t("fullName")} label={t("fullName")} name="name" />
                </div>
                <div className="col-12 mt-2">
                  <label className="form-label">{t("gender")}</label>
                  <br />
                  <Field className="select-role" as="select" name="gender">
                    <option value="male">{t("male")}</option>
                    <option value="female">{t("female")}</option>
                  </Field>
                </div>
                <div className="col-12 mt-2">
                  <TextInput label={t("email")} placeholder="Email" name="email" />
                </div>
                <div className="col-12 mt-2">
                  <label className="form-label">{t("role")}</label>
                  <br />
                  <Field className="select-role" as="select" name="role">
                    <option value="NULL">{t("notAssigned")}</option>
                    {rolesforusers && rolesforusers.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </Field>
                </div>
                <div className="form-group mt-2 text-left">
                  <label className="form-label">{t("password")}</label>
                  <PassWordInput label="Password" placeholder="" name="password" type="password" />
                </div>
                <div className="form-group mt-2 text-left">
                  <label className="form-label">{t("confirmPassword")}</label>
                  <PassWordInput label="Confirm Password" placeholder="" name="confirmPassword" type="password" />
                </div>
                <div className="spacer"></div>
                <div>
                  <div className="float-left">
                    <Button onClick={submitForm}>{t("addUser")}</Button>
                  </div>
                  <div className="float-right">
                    <Button onClick={() => setModalState(false)}>{t("close")}</Button>
                  </div>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </Modal>

      {/* Approval Modal */}
      <ModalApproval show={modalStateApproval !== null}>
        <div>
          <h1>{t("areYouSure")}</h1>
          <div>{t("wantToChangeUserStatus")}</div>
          <div className="spacer"></div>
          <div>
            <div className="float-left">
              <Button onClick={userStatusHandler}>{t("yes")}</Button>
            </div>
            <div className="float-right">
              <Button onClick={() => setModalStateApproval(null)}>{t("no")}</Button>
            </div>
          </div>
        </div>
      </ModalApproval>

      {/* Verify Modal */}
      <ModalVerify show={modalStateVerfiy !== null}>
        <div>
          <h1>{t("areYouSure")}</h1>
          <div>{t("wantToVerifyUser")}</div>
          <div className="spacer"></div>
          <div>
            <div className="float-left">
              <Button onClick={userVerifyHandler}>{t("yes")}</Button>
            </div>
            <div className="float-right">
              <Button onClick={() => setModalStateVerify(null)}>{t("no")}</Button>
            </div>
          </div>
        </div>
      </ModalVerify>

      {/* Activate Modal */}
      <ModalActivate show={modalStateActivate}>
        <div>
          <h1>{t("areYouSure")}</h1>
          <div>{t("wantToActivateUser")}</div>
          <div className="spacer"></div>
          <div>
            <div className="float-left">
              <Button>{t("yes")}</Button>
            </div>
            <div className="float-right">
              <Button onClick={() => setModalStateActivate(false)}>{t("no")}</Button>
            </div>
          </div>
        </div>
      </ModalActivate>

      {/* Password Modal */}
      <ModalPasswordChange show={modalStatePassword}>
        <ChangePassword
          closeModel={() => setModalStatePassword(null)}
          onSubmit={(values) => userPasswordHandler(values)}
          userId={modalStatePassword}
        />
      </ModalPasswordChange>

      {/* Add User Button */}
      <PermissionGate permission={"users.addUser"}>
        <div className="button-bar">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setModalState(true)}
            disabled={!rolesforusers || rolesforusers.length === 0}
          >
            {t("addUser")}
          </Button>
        </div>
      </PermissionGate>

      {/* Search bar */}
      <div className="d-flex align-items-center gap-2 mt-3 mb-2 flex-wrap">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: "240px" }}
          placeholder={t("searchByName")}
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          aria-label="Search by name"
        />
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: "280px" }}
          placeholder={t("searchByEmail")}
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          aria-label="Search by email"
        />
        {hasActiveSearch && (
          <Button variant="outline-secondary" size="sm" onClick={clearSearch}>
            {t("clearSearch")}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="mt-2">
        <div className="table-header">
          <div className="column">#</div>
          <div className="column">ID</div>
          <div className="column">{t("avatar")}</div>
          <div className="column">{t("columnName")}</div>
          <div className="column">{t("columnVerified")}</div>
          <div className="column">{t("email")}</div>
          <div className="column">{t("role")}</div>
          <div className="column">{t("columnDomain")}</div>
          <div className="column">{t("columnStatus")}</div>
          <div className="column">{t("columnActions")}</div>
        </div>

        <div className="table-body">
          {isLoading ? (
            <div className="loading-screen">
              <img src={LOADING} alt="Loading..." />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-4 text-muted">{t("noUsersFound")}</div>
          ) : (
            users.map((item, index) => {
              const profilePicUrl = item.profile_picture_path
                ? API_BASE_URL + item.profile_picture_path.replaceAll("public/uploads", "/storage/uploads")
                : null;
              const defaultImg = item.gender === "male" ? DefaultMaleImage : DefaultFemaleImage;
              const rowNumber = (page - 1) * USERS_PER_PAGE + index + 1;

              return (
                <div key={item.id} className="mobile-table-body">
                  <div className="mobile-table-header">
                    <div className="column">#</div>
                    <div className="column">ID</div>
                    <div className="column">{t("avatar")}</div>
                    <div className="column">{t("columnName")}</div>
                    <div className="column">{t("columnVerified")}</div>
                    <div className="column">{t("email")}</div>
                    <div className="column">{t("role")}</div>
                    <div className="column">{t("columnDomain")}</div>
                    <div className="column">{t("columnStatus")}</div>
                    <div className="column">{t("columnActions")}</div>
                  </div>
                  <div className="table-row">
                    <div className="column">{rowNumber}</div>
                    <div className="column">{item.id}</div>
                    <div className="column">
                      <img
                        className="max-height-profile-pic-manage-users"
                        src={profilePicUrl || defaultImg}
                        alt={`Profile of ${item.name}`}
                      />
                    </div>
                    <div className="column">{item.name}</div>
                    <div className="column">{item.email_verified_at != null ? "true" : "false"}</div>
                    <div className="column">{item.email}</div>
                    <div className="column">{item.roles ? item.roles : "not-assigned"}</div>
                    <div className="column">{item.domain}</div>
                    <div className="column">
                      <FormCheck
                        type="switch"
                        disabled
                        checked={item.is_active === 1}
                        label=""
                        style={{ display: "flex", alignItems: "center", justifyContent: "right" }}
                      />
                    </div>
                    <div className="column">
                      <Dropdown show={menuOpen[index]} onToggle={() => handleToggle(index)}>
                        <Dropdown.Toggle variant="success" id="dropdown-basic">
                          {t("actions")}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className={`mobile-dropdown ${menuOpen[index] ? "visible" : ""}`}>
                          <PermissionGate permission={"users.changePassword"}>
                            <Dropdown.Item onClick={() => setModalStatePassword(item.id)}>
                              {t("changePassword")}
                            </Dropdown.Item>
                          </PermissionGate>
                          <PermissionGate permission={"users.changeRole"}>
                            <Dropdown.Item onClick={() => { setModalStateChangeRole(true); setChangeRoleUserId(item.id); }}>
                              {t("changeRole")}
                            </Dropdown.Item>
                          </PermissionGate>
                          <PermissionGate permission={"users.statusChange"}>
                            <Dropdown.Item onClick={() => setModalStateApproval(item.id)}>
                              {item.is_active === 1 ? t("deactivateUser") : t("activateUser")}
                            </Dropdown.Item>
                          </PermissionGate>
                          <PermissionGate permission={"users.verifyUser"}>
                            <Dropdown.Item onClick={() => setModalStateVerify(item.id)}>
                              {t("verifyUser")}
                            </Dropdown.Item>
                          </PermissionGate>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {!isLoading && (
          <div className="d-flex justify-content-left align-items-center gap-3 mt-3">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              {t("previous")}
            </Button>
            <span>{t("page")} {page} / {totalPages}</span>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              {t("next")}
            </Button>
          </div>
        )}

        <div className="spacer"></div>
      </div>

      {/* Change Role Modal */}
      <Modal show={modalStateChangeRole}>
        <div>
          <h1>{t("changeRole")}</h1>
          <Formik
            initialValues={{ roleId: "" }}
            onSubmit={(values) => changeRole(values)}
          >
            {({ setFieldValue }) => (
              <Form className="row py-4">
                <div className="col-12">
                  <select
                    name="roleId"
                    className="form-control"
                    onChange={(e) => setFieldValue("roleId", e.target.value)}
                  >
                    <option value="NULL">{t("notAssigned")}</option>
                    {roles.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </div>
                <div className="spacer"></div>
                <div>
                  <div className="float-left">
                    <Button type="submit">{t("submit")}</Button>
                  </div>
                  <div className="float-right">
                    <Button type="button" onClick={() => setModalStateChangeRole(false)}>
                      {t("close")}
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal>
    </>
  );
}

export default ManageAdmin;